import {
  Box,
  Card,
  type CustomerQuote,
  Dialog,
  Divider,
  Flex,
  FormControlSizes,
  FormMessage,
  HStack,
  IconButton,
  IconName,
  InlineFormattedNumber,
  MixpanelEvent,
  NumericField,
  PriceInput,
  type Quote,
  request,
  type RequiredProperties,
  type SideEnum,
  Text,
  toBig,
  toBigWithDefault,
  Tooltip,
  useCurrency,
  type UseDisclosureWithContextReturn,
  useEndpointsContext,
  useGlobalToasts,
  useMixpanel,
  useSecurity,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import React, { type ReactNode, useCallback, useMemo, useState } from 'react';
import type { QuoteRowWithOrder } from '../types';
import { getPricingDetailsFromFixing } from './getPricingDetailsFromFixing';

interface UpdateFixingPriceBody {
  Price: string;
  Amount: string;
  FixingDetails: NonNullable<QuoteRowWithOrder['FixingDetails']>;
}

/**
 * Picks out the properties from Quote-like entities that this modal needs. This modal can be used with different types of
 * underlying data, as long as these properties (or their equivalents) exist.
 *
 * The QuoteLike entity also needs an OrderID. The modal expects the user to find and attach this to the the passed entity.
 */
export interface ModifyFixingDialogQuoteLike {
  Symbol?: string;
  Currency?: string;
  AmountCurrency?: string;
  CumQty?: string;
  TradedSide?: SideEnum;
  PricingReference?: string;
  TradedQty?: string;
  TradedPx?: string;
  TradedAmt?: string;
  FixingDetails?: {
    Index?: string;
    Fixing?: string;
  };
}

/** The properties needed to open the dialog */
export interface ModifyFixingProps {
  /** The dialog can be used by both taker and maker users. Define which here. */
  mode: 'maker' | 'taker';
  /** The quote or customer quote entity we're modifying */
  quote: Quote | CustomerQuote;
  /** The talos (customer-) ORDER entity ID we should PATCH */
  orderID: string;
}

type ModifyFixingDialogProps = UseDisclosureWithContextReturn<ModifyFixingProps>;

/** Just a wrapper that conditionally renders the dialog so we only mount with a defined context (quote) */
export const ModifyFixingDialogV1 = ({ context, ...props }: ModifyFixingDialogProps) => {
  // Only render the actual dialog (and its contents) if the context is defined.
  // This also serves to reset state on closing and re-opening from some other quote source
  if (!context) {
    return null;
  }

  return <ModifyFixingDialogContent context={context} key={props.openId} {...props} />;
};

const ModifyFixingDialogContent = ({
  context,
  ...dialogProps
}: RequiredProperties<ModifyFixingDialogProps, 'context'>) => {
  const { quote, orderID, mode } = context;
  const mixpanel = useMixpanel();
  const { orgApiEndpoint } = useEndpointsContext();
  const { add: addToast } = useGlobalToasts();

  const [fixingPrice, setFixingPrice] = useState<NumericField>(
    new NumericField({
      name: 'Fixing',
      value: quote.FixingDetails?.Fixing ?? '',
      isRequired: true,
    })
  );
  const fixingPriceChanged = useMemo(
    () => !toBigWithDefault(fixingPrice.value, 0).eq(toBigWithDefault(quote.FixingDetails?.Fixing, 0)),
    [fixingPrice, quote]
  );

  const [price, setPrice] = useState<NumericField>(
    new NumericField({
      name: 'Price',
      value: quote.TradedPx ?? '',
      isRequired: true,
    })
  );
  const priceChanged = useMemo(
    () => !toBigWithDefault(price.value, 0).eq(toBigWithDefault(quote.TradedPx, 0)),
    [quote, price]
  );

  const [amount, setAmount] = useState<NumericField>(
    new NumericField({
      name: 'Amount',
      value: quote.TradedAmt ?? '',
      isRequired: true,
    })
  );
  const amountChanged = useMemo(
    () => !toBigWithDefault(amount.value, 0).eq(toBigWithDefault(quote.TradedAmt, 0)),
    [quote, amount]
  );

  const handleFixingPriceChange = useCallback((value: string | undefined) => {
    setFixingPrice(curr => curr.updateValue(value).setTouched(true).validate());
  }, []);

  const handlePriceChange = useCallback((value: string | undefined) => {
    setPrice(curr => curr.updateValue(value).setTouched(true).validate());
  }, []);

  const handleAmountChange = useCallback((value: string | undefined) => {
    setAmount(curr => curr.updateValue(value).setTouched(true).validate());
  }, []);

  const handleConfirm = useCallback(() => {
    mixpanel.track(MixpanelEvent.ModifyFixing);

    if (!quote || !fixingPrice.value || !price.value || !amount.value) {
      addToast({
        variant: 'NEGATIVE',
        text: 'Something went wrong when attempting to modify fixing. Please try again, or contact support.',
      });
      return;
    }

    const url =
      mode === 'taker'
        ? `${orgApiEndpoint}/orders/${orderID}/update-fixing-price`
        : `${orgApiEndpoint}/customer/orders/${orderID}/update-fixing-price`;

    request('PATCH', url, {
      Price: price.value,
      Amount: amount.value,
      FixingDetails: {
        ...quote.FixingDetails,
        Fixing: fixingPrice.value,
      },
    } satisfies UpdateFixingPriceBody)
      .then(() => {
        addToast({
          text: 'Successfully modified fixing details.',
          variant: 'POSITIVE',
        });
      })
      .catch((e: ErrorEvent) => {
        addToast({
          text: `Failed to modify fixing details: ${e.message}`,
          variant: 'NEGATIVE',
        });
      });
  }, [addToast, orgApiEndpoint, fixingPrice, price, amount, quote, mixpanel, mode, orderID]);

  const anyError = [fixingPrice, price, amount].some(field => field.hasError);
  const anyFieldChanged = fixingPriceChanged || priceChanged || amountChanged;

  // Disabled if nothing has changed, or there is any error
  const confirmDisabled = !anyFieldChanged || anyError;

  return (
    <Dialog
      {...dialogProps}
      confirmLabel="Modify"
      cancelLabel="Close"
      onConfirm={handleConfirm}
      title="Modify Fixing Details"
      width="450px"
      stretchButtons
      confirmDisabled={confirmDisabled}
    >
      {/* Current fixing details shown at the top  */}
      <CurrentStateDetailsCard quoteLike={quote} />

      <Divider orientation="horizontal" my="spacing12" />

      {/* Modification part where we let the user make edits */}
      <ModificationDetailsCard
        quoteLike={quote}
        fixingPriceField={fixingPrice}
        onFixingPriceChange={handleFixingPriceChange}
        priceField={price}
        onPriceChange={handlePriceChange}
        amountField={amount}
        onAmountChange={handleAmountChange}
      />
    </Dialog>
  );
};

/** Just renders and shows the current state. This is used as a reference for the user while they prepare their modification */
const CurrentStateDetailsCard = ({ quoteLike }: { quoteLike: ModifyFixingDialogQuoteLike | undefined }) => {
  const bps = toBig(quoteLike?.PricingReference)?.times(10_000);

  return (
    <Card>
      <Flex flexDirection="column" gap="spacing8" data-testid="modify-fixing-dialog-overview">
        <Text alignSelf="flex-start">Current details</Text>
        <HStack justifyContent="space-between">
          <Text color="colorTextImportant">Index</Text>
          <Text>{quoteLike?.FixingDetails?.Index}</Text>
        </HStack>

        <HStack justifyContent="space-between">
          <Text color="colorTextImportant">Fixing</Text>
          <InlineFormattedNumber
            number={quoteLike?.FixingDetails?.Fixing}
            currency={quoteLike?.AmountCurrency}
            truncate={false}
            align="right"
          />
        </HStack>

        <HStack justifyContent="space-between">
          <Text color="colorTextImportant">Quote</Text>
          <InlineFormattedNumber number={bps} currency="BPS" />
        </HStack>

        <HStack justifyContent="space-between">
          <Text color="colorTextImportant">Price</Text>
          <InlineFormattedNumber
            number={quoteLike?.TradedPx}
            currency={quoteLike?.AmountCurrency}
            truncate={false}
            align="right"
          />
        </HStack>

        <HStack justifyContent="space-between">
          <Text color="colorTextImportant">Quantity</Text>
          <InlineFormattedNumber
            number={quoteLike?.TradedQty}
            currency={quoteLike?.Currency}
            align="right"
            truncate={false}
          />
        </HStack>

        <HStack justifyContent="space-between">
          <Text color="colorTextImportant">Total</Text>
          <InlineFormattedNumber
            number={quoteLike?.TradedAmt}
            currency={quoteLike?.AmountCurrency}
            align="right"
            truncate={false}
          />
        </HStack>
      </Flex>
    </Card>
  );
};

const ModificationDetailsCard = ({
  quoteLike,

  fixingPriceField,
  onFixingPriceChange,
  priceField,
  onPriceChange,
  amountField,
  onAmountChange,
}: {
  quoteLike: ModifyFixingDialogQuoteLike | undefined;

  fixingPriceField: NumericField;
  onFixingPriceChange: (value: string | undefined) => void;
  priceField: NumericField;
  onPriceChange: (value: string | undefined) => void;
  amountField: NumericField;
  onAmountChange: (value: string | undefined) => void;
}) => {
  const details = usePricingDetailsFromFixing(fixingPriceField.value, quoteLike);
  if (!details) {
    return 'Failed to calculate pricing details';
  }

  // These details are what is computed from the entered fixing price. We use these to tell the user that there's a misalignment
  // between what is entered and what is computed. We let the user re-sync px and amt to these values.
  // Note: the computedPx is rounded
  const { px: computedPx, amt: computedAmt, bps } = details;

  const pxIsOff = priceField.bigValue ? !priceField.bigValue.eq(computedPx) : true;
  const pxIsOffWarning = pxIsOff ? (
    <>
      Differs from computed price <InlineFormattedNumber number={computedPx} currency={quoteLike?.AmountCurrency} />
    </>
  ) : undefined;

  const amtIsOff = amountField.bigValue ? !amountField.bigValue.eq(computedAmt) : true;
  const amtIsOffWarning = amtIsOff ? (
    <>
      Differs from computed amount <InlineFormattedNumber number={computedAmt} currency={quoteLike?.AmountCurrency} />
    </>
  ) : undefined;

  // We have temporarily disabled the dps warnings until we have precisions defined on the fixing indices themselves. Keeping the code around so its easier to add later.
  /*   const fixingPriceDpsWarning = exceedsMaxPrecision(fixingPriceField.displayValue, security?.MinPriceIncrement)
    ? 'Exceeds max dps'
    : undefined;
  const pxDpsWarning = exceedsMaxPrecision(priceField.displayValue, security?.MinPriceIncrement)
    ? 'Exceeds max dps'
    : undefined;
  const amtDpsWarning = exceedsMaxPrecision(amountField.displayValue, amountCurrency?.MinIncrement)
    ? 'Exceeds max dps'
    : undefined; */

  // Collect the different warnings we've computed above to be rendered below their respective input fields
  const fixingPriceWarnings = compact([
    /* fixingPriceDpsWarning */
  ]);
  const priceWarnings = compact([pxIsOffWarning /* pxDpsWarning */]);
  const amountWarnings = compact([amtIsOffWarning /* amtDpsWarning */]);

  return (
    <Card>
      <Flex flexDirection="column" gap="spacing8" data-testid="modify-fixing-dialog-editing">
        <Text alignSelf="flex-start">Modification details</Text>
        <HStack justifyContent="space-between">
          <Text color="colorTextImportant">Index</Text>
          <Text>{quoteLike?.FixingDetails?.Index}</Text>
        </HStack>

        <Box>
          <HStack justifyContent="space-between">
            <Text color="colorTextImportant">Fixing</Text>
            <PriceInput
              value={fixingPriceField.displayValue}
              onChange={onFixingPriceChange}
              width="200px"
              data-testid="fixing-price-input"
              invalid={fixingPriceField.hasError}
              currency={quoteLike?.AmountCurrency}
            />
          </HStack>

          <MultiWarning
            errorString={fixingPriceField.errorString}
            warnings={fixingPriceWarnings}
            testID="fixing-price-input-warning"
          />
        </Box>

        <HStack justifyContent="space-between">
          <Text color="colorTextImportant">Quote</Text>
          <InlineFormattedNumber number={bps} currency="BPS" />
        </HStack>

        <Box>
          <HStack justifyContent="space-between">
            <Text color="colorTextImportant">Price</Text>
            <PriceInput
              value={priceField.displayValue}
              onChange={onPriceChange}
              width="200px"
              data-testid="price-input"
              invalid={priceField.hasError}
              currency={quoteLike?.AmountCurrency}
              prefix={
                pxIsOff && (
                  <Tooltip tooltip="Set to computed amount">
                    <IconButton
                      ghost
                      size={FormControlSizes.Small}
                      onClick={() => onPriceChange(computedPx.toFixed())}
                      icon={IconName.Refresh}
                      data-testid="price-input-refresh-button"
                    />
                  </Tooltip>
                )
              }
            />
          </HStack>

          <MultiWarning errorString={priceField.errorString} warnings={priceWarnings} testID="price-input-warning" />
        </Box>

        <HStack justifyContent="space-between">
          <Text color="colorTextImportant">Quantity</Text>
          <InlineFormattedNumber number={quoteLike?.TradedQty} currency={quoteLike?.Currency} align="right" />
        </HStack>

        <Box>
          <HStack justifyContent="space-between">
            <Text color="colorTextImportant">Total</Text>
            <PriceInput
              value={amountField.displayValue}
              onChange={onAmountChange}
              width="200px"
              data-testid="amount-input"
              invalid={amountField.hasError}
              currency={quoteLike?.AmountCurrency}
              prefix={
                amtIsOff && (
                  <Tooltip tooltip="Set to computed amount">
                    <IconButton
                      ghost
                      size={FormControlSizes.Small}
                      onClick={() => onAmountChange(computedAmt.toFixed())}
                      icon={IconName.Refresh}
                      data-testid="amount-input-refresh-button"
                    />
                  </Tooltip>
                )
              }
            />
          </HStack>
          <MultiWarning errorString={amountField.errorString} warnings={amountWarnings} testID="amount-input-warning" />
        </Box>
      </Flex>
    </Card>
  );
};

// Maybe wanna make an actual component out of this later. For now its too coupled
const MultiWarning = ({
  errorString,
  warnings,
  testID,
}: {
  errorString?: string;
  warnings: ReactNode[];
  testID?: string;
}) => {
  return (
    <Text textAlign="right" data-testid={testID}>
      {errorString ? (
        <FormMessage error>{errorString}</FormMessage>
      ) : warnings.length > 0 ? (
        <FormMessage warning>
          {warnings.map((warning, index) => (
            <React.Fragment key={index}>
              {warning}
              {index !== warnings.length - 1 ? ', ' : null}
              {/* show a comma if we are not the last warning item rendered */}
            </React.Fragment>
          ))}
        </FormMessage>
      ) : (
        <></>
      )}
    </Text>
  );
};

const usePricingDetailsFromFixing = (
  fixingPrice: string | undefined,
  quote: ModifyFixingDialogQuoteLike | undefined
) => {
  const security = useSecurity(quote?.Symbol);
  const amountCurrency = useCurrency(quote?.AmountCurrency);
  return useMemo(
    () =>
      getPricingDetailsFromFixing({
        fixingPrice,
        quote,
        priceIncrement: security?.MinPriceIncrement,
        amountIncrement: amountCurrency?.MinIncrement,
      }),
    [fixingPrice, quote, security?.MinPriceIncrement, amountCurrency?.MinIncrement]
  );
};
